import React, { useMemo, useState } from "react"

import { publicSectionClasses } from "@/components/public/venue/section_styles"
import ShowMapModal from "@/components/public/venue/show_map_modal"
import VenueHeader from "@/components/public/venue/venue_header"
import VenueInfo from "@/components/public/venue/venue_info"
import VenueInfoModal from "@/components/public/venue/venue_info_modal"
import VenueOpeningHours from "@/components/public/venue/venue_opening_hours"
import VenueRuleSection from "@/components/public/venue/venue_rule_section"
import VenueSocialAccounts from "@/components/public/venue/venue_social_accounts"
import VenueTags from "@/components/venue_tags"
import type { VenueTagDisplay, TagCategory } from "@/components/venue_tags/types"
import { CATEGORY_DISPLAY_ORDER } from "@/constants/venue_tags"
import i18n from "@/i18n"
import { buildBookingPolicyTiles, parseBookingPolicyTileKinds } from "@/shared/booking_policy"
import useIsMobile from "@/shared/use_is_mobile"

import type { VenueType } from "./types"

const STYLES = {
  openingHoursSection: "grid grid-cols-1 gap-8",
} as const

type Props = {
  venue: VenueType
  onBookTable?: () => void
  bookButtonRef?: React.Ref<HTMLButtonElement>
}

type SocialAccounts = Record<string, string>

const TAG_CATEGORY_VALUES: ReadonlySet<string> = new Set(CATEGORY_DISPLAY_ORDER)

const isTagCategory = (value: string): value is TagCategory => TAG_CATEGORY_VALUES.has(value)

const parseSocialAccounts = (value: unknown): SocialAccounts | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null

  const parsed = Object.entries(value).reduce<SocialAccounts>((accounts, [key, accountUrl]) => {
    if (typeof accountUrl === "string" && accountUrl.trim()) {
      accounts[key] = accountUrl
    }

    return accounts
  }, {})

  return Object.keys(parsed).length > 0 ? parsed : null
}

const VenueContent: React.FC<Props> = ({ venue, onBookTable, bookButtonRef }) => {
  const isMobile = useIsMobile()
  const [infoModalOpen, setInfoModalOpen] = useState(false)
  const [showMapOpen, setShowMapOpen] = useState(false)
  const tagCategories: VenueTagDisplay[] = useMemo(() => {
    if (!venue.tags?.length) return []
    const grouped: Partial<Record<TagCategory, string[]>> = {}
    for (const tag of venue.tags) {
      if (!isTagCategory(tag.category)) continue
      const cat = tag.category
      if (!grouped[cat]) grouped[cat] = []
      grouped[cat].push(tag.label)
    }
    return CATEGORY_DISPLAY_ORDER.reduce<VenueTagDisplay[]>((acc, key) => {
      const values = grouped[key]
      if (values?.length) {
        acc.push({
          category: key,
          label: i18n.t(`app.venue_tags.categories.${key}`),
          values: values.join(", "),
        })
      }
      return acc
    }, [])
  }, [venue.tags])

  const plansWithContent = useMemo(() => venue.plans?.filter((m) => m.plan) || [], [venue.plans])
  const availableBookingPolicyTiles = useMemo(
    () => parseBookingPolicyTileKinds(venue.reservationConfiguration?.availableBookingPolicyTiles),
    [venue.reservationConfiguration?.availableBookingPolicyTiles],
  )
  const bookingPolicyTilesConfig = useMemo(
    () => parseBookingPolicyTileKinds(venue.reservationConfiguration?.bookingPolicyTiles),
    [venue.reservationConfiguration?.bookingPolicyTiles],
  )
  const socialAccounts = useMemo(() => parseSocialAccounts(venue.info?.socialAccounts), [venue.info?.socialAccounts])

  const bookingPolicyTiles = useMemo(
    () =>
      buildBookingPolicyTiles(
        {
          availableBookingPolicyTiles,
          bookingPolicyTiles: bookingPolicyTilesConfig,
          minReservationTime: venue.reservationConfiguration?.minReservationTime,
          maxReservationTime: venue.reservationConfiguration?.maxReservationTime,
          noshowAutoCancelAfter: venue.reservationConfiguration?.noshowAutoCancelAfter,
          largeGroupThreshold: venue.reservationConfiguration?.largeGroupThreshold,
          cancellationDeadline: venue.reservationConfiguration?.cancellationDeadline,
          phone: venue.info?.phone,
          email: venue.info?.email,
        },
        { includeDisabled: false, includeUnconfigured: false },
      ),
    [availableBookingPolicyTiles, bookingPolicyTilesConfig, venue.reservationConfiguration, venue.info],
  )

  const sortedSections = useMemo(
    () => [...(venue.reservationRuleSections || [])].sort((a, b) => a.position - b.position),
    [venue.reservationRuleSections],
  )

  const bookingPolicyContent = useMemo(
    () => sortedSections.find((section) => section.identifier === "booking_policy")?.content,
    [sortedSections],
  )

  const hasCoordinates = venue.info?.latitude != null && venue.info?.longitude != null

  return (
    <>
      <VenueHeader
        name={venue.name}
        address={venue.info?.address}
        zipcode={venue.info?.zipcode}
        city={venue.info?.city}
        tags={venue.tags}
        openingHours={venue.openingHours || []}
        timezone={venue.timezone}
        onBookClick={onBookTable}
        onInfoClick={isMobile ? () => setInfoModalOpen(true) : undefined}
        onShowMapClick={isMobile && hasCoordinates ? () => setShowMapOpen(true) : undefined}
        bookButtonRef={bookButtonRef}
      />

      <VenueTags categories={tagCategories} variant="inline" />

      {sortedSections.map((section, index) => (
        <VenueRuleSection
          key={section.id}
          section={section}
          venue={venue}
          bookingPolicyTiles={bookingPolicyTiles}
          plansWithContent={plansWithContent}
          onBookTable={onBookTable}
          showDivider={index > 0}
        />
      ))}

      <div
        className={`${publicSectionClasses(isMobile)} ${STYLES.openingHoursSection}`}
        data-testid="public-venue-opening-hours-section"
      >
        <VenueOpeningHours openingHours={venue.openingHours || []} timezone={venue.timezone} />
      </div>

      <VenueInfo
        address={venue.info?.address}
        zipcode={venue.info?.zipcode}
        city={venue.info?.city}
        country={venue.country}
        phone={venue.info?.phone}
        email={venue.info?.email}
        website={venue.info?.website}
        venueName={venue.name}
        latitude={venue.info?.latitude}
        longitude={venue.info?.longitude}
        socialAccounts={<VenueSocialAccounts socialAccounts={socialAccounts} />}
      />

      {!!isMobile && (
        <>
          <VenueInfoModal
            open={infoModalOpen}
            onClose={() => setInfoModalOpen(false)}
            venueName={venue.name}
            address={venue.info?.address}
            zipcode={venue.info?.zipcode}
            city={venue.info?.city}
            country={venue.country}
            phone={venue.info?.phone}
            email={venue.info?.email}
            website={venue.info?.website}
            latitude={venue.info?.latitude}
            longitude={venue.info?.longitude}
            socialAccounts={socialAccounts}
            highlights={tagCategories}
            openingHours={venue.openingHours || []}
            timezone={venue.timezone}
            bookingPolicyTiles={bookingPolicyTiles}
            bookingPolicyContent={bookingPolicyContent}
          />
          {!!hasCoordinates && (
            <ShowMapModal
              open={showMapOpen}
              onClose={() => setShowMapOpen(false)}
              venueName={venue.name}
              latitude={venue.info.latitude}
              longitude={venue.info.longitude}
            />
          )}
        </>
      )}
    </>
  )
}

export default VenueContent
